import { faBicycle } from '@fortawesome/pro-regular-svg-icons/faBicycle';
import { faBus } from '@fortawesome/pro-regular-svg-icons/faBus';
import { faCalendar } from '@fortawesome/pro-regular-svg-icons/faCalendar';
import { faCarSide } from '@fortawesome/pro-regular-svg-icons/faCarSide';
import { faClock } from '@fortawesome/pro-regular-svg-icons/faClock';
import { faClockEightThirty } from '@fortawesome/pro-regular-svg-icons/faClockEightThirty';
import { faTimes } from '@fortawesome/pro-regular-svg-icons/faTimes';
import { faWalking } from '@fortawesome/pro-regular-svg-icons/faWalking';
import {
  TIME_OPTIONS,
  arrivalDayOptions,
  arrivalTimeOptions,
  validationSchema,
} from '@propertypal/shared/src/constants/map';
import useDebounce from '@propertypal/shared/src/hooks/useDebounce';
import { useFormik } from 'formik';
import React, { CSSProperties, FunctionComponent, useEffect } from 'react';
import { useTheme } from 'styled-components';
import Button from '../buttons/Button';
import FontAwesomeIcon from '../icons/FontAwesomeIcon';
import SelectInput from '../inputs/SelectInput';
import TextInput from '../inputs/TextInput';
import { Box, Row } from '../layout';
import { MidHeading } from '../typography';
import { ArrivalRow, Container, LowerRow, TravelOption } from './TravelSearchBox.style';

interface Props {
  containerStyle?: CSSProperties;
  title?: string;
  loading?: boolean;
  onUpdate: (location: string, time: string, mode: string, arriveDay: string, arriveTime: string) => void;
  onSubmit?: () => void;
  error?: string;
  onClose?: () => void;
}

const UPDATE_DELAY = process?.env?.JEST_WORKER_ID ? 0 : 500;

const TravelSearchBox: FunctionComponent<Props> = (props) => {
  const theme = useTheme();

  const formik = useFormik({
    initialValues: {
      location: '',
      mode: 'drive',
      time: '',
      arrivalDay: '1',
      arrivalTime: '',
    },
    validationSchema,
    onSubmit: () => {
      if (props.onSubmit) {
        props.onSubmit();
      }
    },
  });

  const handleModeChange = (mode: string) => () => {
    formik.handleChange('mode')(mode);
  };

  const handleUpdateDebounce = useDebounce(props.onUpdate, UPDATE_DELAY, { leading: false, trailing: true }, [
    props.onUpdate,
  ]);

  useEffect(() => {
    formik.validateForm(formik.values).then((errors) => {
      if (Object.keys(errors).length === 0) {
        handleUpdateDebounce(
          formik.values.location,
          formik.values.time,
          formik.values.mode,
          formik.values.arrivalDay,
          formik.values.arrivalTime,
        );
      }
    });
  }, [
    formik.values.location,
    formik.values.time,
    formik.values.mode,
    formik.values.arrivalDay,
    formik.values.arrivalTime,
  ]);

  return (
    <Container style={props.containerStyle} onSubmit={formik.handleSubmit}>
      {props.title && (
        <Row style={{ justifyContent: 'space-between' }}>
          <MidHeading fontSize={14}>{props.title}</MidHeading>

          {props.onClose && (
            <button type="button" onClick={props.onClose}>
              <FontAwesomeIcon icon={faTimes} color={theme.textDark} style={{ fontSize: 20 }} />
            </button>
          )}
        </Row>
      )}

      <TextInput
        value={formik.values.location}
        onValueChange={formik.handleChange('location')}
        name="travelSearchText"
        placeholder="Enter a postcode, place name or address"
        error={(formik.touched.location && formik.errors.location) || props.error}
        testID="travelSearchBoxInput"
      />

      <LowerRow>
        <Row>
          <TravelOption
            title="Driving"
            aria-label="Car"
            active={formik.values.mode === 'drive'}
            onClick={handleModeChange('drive')}
            type="button"
            data-testid="transport-drive"
          >
            <FontAwesomeIcon icon={faCarSide} color={theme.white} style={{ fontSize: 18 }} />
          </TravelOption>

          <TravelOption
            title="Walking"
            aria-label="Walk"
            active={formik.values.mode === 'walk'}
            onClick={handleModeChange('walk')}
            type="button"
            data-testid="transport-walk"
          >
            <FontAwesomeIcon icon={faWalking} color={theme.white} style={{ fontSize: 18 }} />
          </TravelOption>

          <TravelOption
            title="Cycling"
            aria-label="Bicycle"
            active={formik.values.mode === 'bicycle'}
            onClick={handleModeChange('bicycle')}
            type="button"
            data-testid="transport-bicycle"
          >
            <FontAwesomeIcon icon={faBicycle} color={theme.white} style={{ fontSize: 18 }} />
          </TravelOption>

          <TravelOption
            title="Public Transport"
            aria-label="Public Transport"
            active={formik.values.mode === 'transit'}
            onClick={handleModeChange('transit')}
            type="button"
            data-testid="transport-transit"
          >
            <FontAwesomeIcon icon={faBus} color={theme.white} style={{ fontSize: 18 }} />
          </TravelOption>
        </Row>

        <SelectInput
          inputStyle={{ height: 42 }}
          icon={faClock}
          ariaLabel="Travel time"
          placeholder="Select a travel time"
          id="travelTime"
          options={TIME_OPTIONS}
          value={formik.values.time}
          onChange={formik.handleChange('time')}
          testID="travelTimeSelect"
        />
      </LowerRow>

      {(formik.values.mode === 'drive' || formik.values.mode === 'transit') && (
        <ArrivalRow
          $dayError={!!(formik.touched.arrivalDay && formik.errors.arrivalDay)}
          $timeError={!!(formik.touched.arrivalTime && formik.errors.arrivalTime)}
          $isDrive={formik.values.mode === 'drive'}
        >
          <Box flexDirection="row" display="flex">
            {formik.values.mode !== 'drive' && (
              <SelectInput
                inputStyle={{ height: 42 }}
                icon={faCalendar}
                ariaLabel="Travel Arrival Day"
                placeholder="Arrival day"
                id="arrivalDay"
                options={arrivalDayOptions}
                value={formik.values.arrivalDay}
                onChange={(v) => {
                  formik.setFieldTouched('arrivalTime');
                  formik.handleChange('arrivalDay')(v);
                }}
                testID="arrivalDaySelect"
                error={formik.touched.arrivalDay && formik.errors.arrivalDay}
              />
            )}
            <SelectInput
              inputStyle={{ height: 42 }}
              icon={faClockEightThirty}
              ariaLabel="Travel Arrival Time"
              placeholder="Arrival time"
              id="arrivalTime"
              options={arrivalTimeOptions}
              value={formik.values.arrivalTime}
              onChange={(v) => {
                formik.setFieldTouched('arrivalDay');
                formik.handleChange('arrivalTime')(v);
              }}
              testID="arrivalTimeSelect"
              error={formik.touched.arrivalTime && formik.errors.arrivalTime}
            />
          </Box>
        </ArrivalRow>
      )}

      {props.onSubmit && (
        <Button containerStyle={{ marginTop: 5 }} loading={props.loading} type="submit">
          Add to Search
        </Button>
      )}
    </Container>
  );
};

export default TravelSearchBox;
